import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

interface CacheEntry {
  body: any;
  headers: Record<string, string>;
  status: number;
  expiry: number;
}

const CACHE_PREFIX = 'http-cache:';

@Injectable({ providedIn: 'root' })
export class HttpCacheService {
  get(key: string): HttpResponse<any> | null {
    const raw = localStorage.getItem(CACHE_PREFIX + key);

    if (!raw) {
      return null;
    }

    try {
      const entry: CacheEntry = JSON.parse(raw);

      if (Date.now() > entry.expiry) {
        localStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }

      return new HttpResponse({
        body: entry.body,
        headers: new HttpHeaders(entry.headers),
        status: entry.status,
      });
    } catch {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
  }

  set(key: string, response: HttpResponse<any>, ttlMs: number): void {
    if (!key.includes('v3/')) return;
    const entry: CacheEntry = {
      body: response.body,
      headers: this.serializeHeaders(response.headers),
      status: response.status,
      expiry: Date.now() + ttlMs,
    };

    try {
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // Storage quota exceeded or serialization issue
      // Fail silently by design
    }
  }

  invalidate(urlPrefix: string): void {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX + urlPrefix))
      .forEach((k) => localStorage.removeItem(k));
  }

  clear(): void {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  }

  private serializeHeaders(headers: any): Record<string, string> {
    const result: Record<string, string> = {};
    headers.keys().forEach((key) => {
      result[key] = headers.get(key)!;
    });
    return result;
  }
}
